/**
 * Plan Intent Cookie Utilities
 *
 * Stores the user's selected subscription plan intent (pro_monthly, pro_annual)
 * in a cookie so it survives OAuth redirects and email verification during signup.
 * Follows the same pattern as src/lib/promo-cookie.ts.
 */

const PLAN_INTENT_COOKIE_NAME = 'ipl_plan_intent'
const COOKIE_MAX_AGE_SECONDS = 24 * 60 * 60 // 24 hours

export type PlanIntent = 'pro_monthly' | 'pro_annual'

const VALID_PLANS: readonly string[] = ['pro_monthly', 'pro_annual']

/**
 * Set the plan intent cookie (client-side)
 */
export function setPlanIntentCookie(plan: string): void {
  if (typeof document === 'undefined') return
  if (!VALID_PLANS.includes(plan)) return

  document.cookie = `${PLAN_INTENT_COOKIE_NAME}=${encodeURIComponent(plan)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`
}

/**
 * Get the plan intent from cookie (client-side)
 */
export function getPlanIntentCookie(): PlanIntent | null {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === PLAN_INTENT_COOKIE_NAME && value) {
      const decoded = decodeURIComponent(value)
      if (VALID_PLANS.includes(decoded)) {
        return decoded as PlanIntent
      }
    }
  }
  return null
}

/**
 * Clear the plan intent cookie (client-side)
 * Call this after the plan has been acted on in the onboarding wizard.
 */
export function clearPlanIntentCookie(): void {
  if (typeof document === 'undefined') return

  document.cookie = `${PLAN_INTENT_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
}

/**
 * Get the plan intent from cookie (server-side)
 * For use in server components or API routes.
 */
export function getPlanIntentFromCookies(cookieHeader: string | null): PlanIntent | null {
  if (!cookieHeader) return null

  const cookies = cookieHeader.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === PLAN_INTENT_COOKIE_NAME && value) {
      const decoded = decodeURIComponent(value)
      if (VALID_PLANS.includes(decoded)) {
        return decoded as PlanIntent
      }
    }
  }
  return null
}

/**
 * Cookie name export for server-side operations
 */
export const PLAN_INTENT_COOKIE = PLAN_INTENT_COOKIE_NAME
